import React, { useEffect, useState, useMemo } from "react";
import { Modal, Input } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import * as Solid from "@fortawesome/free-solid-svg-icons";
import { SubmitButton } from "components/shared/general";
import { useTranslation } from "react-i18next";

const PAGE_SIZE = 120; // how many icons to render at once in modal

// Build ICONS dictionary from FontAwesome Solid icons
const extractIcons = (iconSet, prefix) =>
  Object.entries(iconSet)
    .filter(([, icon]) => icon?.iconName)
    .reduce((acc, [key, icon]) => {
      acc[key] = {
        ...icon,
        faClass: `${prefix} fa-${icon.iconName}`, // e.g. "fas fa-wrench"
      };
      return acc;
    }, {});

export const ICONS = extractIcons(Solid, "fas");

/**
 * Helper to resolve a FontAwesome icon object from a string value
 * (e.g. "fas fa-wrench" or "wrench")
 */
export const getFaIcon = (value) => {
  if (!value) return null;
  const q = String(value).trim();
  const entry = Object.values(ICONS).find(
    (icon) =>
      icon.faClass === q ||
      icon.iconName === q ||
      `fa-${icon.iconName}` === q ||
      `fas fa-${icon.iconName}` === q
  );
  return entry || null;
};

/**
 * Shared IconPicker component controlled by `value` + `onChange`
 * Compatible with Ant Design Form.Item
 *
 * value = string e.g. "fas fa-wrench" or "wrench"
 */
const IconPicker = ({
  value,
  onChange,
  t: customT,
  placeholder,
  disabled = false,
  className = "",
}) => {
  const { t: i18nT } = useTranslation();
  const t = customT || i18nT;

  const [open, setOpen] = useState(false);
  const [tempSelection, setTempSelection] = useState(null);
  const [search, setSearch] = useState("");
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  const iconEntries = useMemo(() => Object.entries(ICONS), []);

  // Find matching icon entry based on value
  const currentIcon = useMemo(() => getFaIcon(value), [value]);

  const currentIconKey = useMemo(() => {
    if (!currentIcon) return null;
    const entry = iconEntries.find(([, icon]) => icon === currentIcon);
    return entry?.[0] || null;
  }, [currentIcon, iconEntries]);

  const handleOpen = () => {
    if (disabled) return;
    setTempSelection(currentIconKey);
    setSearch("");
    setOpen(true);
  };

  const handleCancel = () => {
    setOpen(false);
  };

  const handleConfirm = () => {
    if (tempSelection) {
      const selected = ICONS[tempSelection];
      onChange?.(selected.faClass);
    } else {
      onChange?.(null);
    }
    setOpen(false);
  };

  const filteredIcons = useMemo(() => {
    if (!search) return iconEntries;
    const q = search.toLowerCase();
    return iconEntries.filter(
      ([key, icon]) =>
        key.toLowerCase().includes(q) ||
        icon?.iconName?.toLowerCase().includes(q)
    );
  }, [iconEntries, search]);

  useEffect(() => {
    if (open) {
      setVisibleCount(PAGE_SIZE);
    }
  }, [open, search]);

  const visibleIcons = filteredIcons.slice(0, visibleCount);
  const hasMore = filteredIcons.length > visibleCount;

  const handleLoadMore = () => {
    setVisibleCount((prev) => prev + PAGE_SIZE);
  };

  return (
    <>
      {/* Clickable trigger field */}
      <div
        onClick={handleOpen}
        className={[
          "inline-flex h-[42px] w-full items-center justify-between gap-2 rounded-md border border-border-muted bg-surface px-3 py-2 text-sm text-heading transition",
          disabled
            ? "opacity-50 cursor-not-allowed"
            : "cursor-pointer hover:border-primary",
          className,
        ].join(" ")}
      >
        <div className="flex items-center gap-2.5 truncate">
          {currentIcon ? (
            <>
              <FontAwesomeIcon
                icon={currentIcon}
                className="text-base text-primary"
              />
              <span className="truncate font-medium">
                {currentIcon.iconName}
              </span>
            </>
          ) : (
            <span className="text-text-subtle font-normal">
              {placeholder || t("chooseIcon")}
            </span>
          )}
        </div>
        {currentIcon && !disabled && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onChange?.(null);
            }}
            className="text-xs text-text-subtle hover:text-danger px-1"
            title={t("button.delete")}
          >
            ✕
          </button>
        )}
      </div>

      {/* Modal with search + scrollable icon grid */}
      <Modal
        open={open}
        onCancel={handleCancel}
        footer={false}
        width={700}
        title={t("chooseIcon")}
      >
        {/* Search input */}
        <Input
          allowClear
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t("search_placeholder")}
          className="my-4"
        />

        <div className="max-h-[480px] scrollbar-hidden overflow-y-auto p-2 mb-4">
          <div className="grid gap-3 [grid-template-columns:repeat(auto-fill,minmax(85px,1fr))]">
            {visibleIcons.map(([key, icon]) => {
              const isSelected = tempSelection === key;
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => setTempSelection(key)}
                  title={icon.iconName}
                  className={[
                    "group flex flex-col items-center justify-center rounded-xl p-3 text-center transition-all",
                    isSelected
                      ? "bg-primary/10 text-primary shadow-sm ring-2 ring-primary"
                      : "bg-surface text-heading hover:bg-surface-muted hover:shadow-sm border border-border-light",
                  ].join(" ")}
                >
                  <div className="flex h-9 w-9 items-center justify-center">
                    <FontAwesomeIcon icon={icon} className="text-xl" />
                  </div>
                  <span className="text-[10px] text-text-muted truncate max-w-full mt-1">
                    {icon.iconName}
                  </span>
                </button>
              );
            })}
          </div>
          {hasMore && (
            <div className="mt-4 flex justify-center">
              <button
                type="button"
                onClick={handleLoadMore}
                className="rounded-md border border-border-muted bg-surface px-4 py-1.5 text-xs font-medium text-heading hover:bg-surface-muted"
              >
                {t("load_more")}
              </button>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 border-t border-border-light pt-3">
          <SubmitButton onClick={handleConfirm}>
            {t("button.save")}
          </SubmitButton>
        </div>
      </Modal>
    </>
  );
};

export default IconPicker;
